import { ChangeEvent, useEffect, useState } from "react";
import { Vehicle } from "../../types/vehicle.type";
import { createVehicle, updateVehicle } from "../../services";
import { FieldLabel, inputSx } from "../Product/shared/FormField";
import {
  FUEL_TYPE_OPTIONS,
  INITIAL_VEHICLE_FORM,
  VEHICLE_PLATE_EXAMPLE,
  VEHICLE_TEXT_MAX_LENGTH,
  VehicleForm,
  VehicleFormErrors,
  toVehicleForm,
  toVehicleRequest,
  validateVehicleForm,
} from "./vehicleForm";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { COMPACT_QUERY } from "../../utils/hooks/useCompact";

const compact = "@media " + COMPACT_QUERY;

const DRAWER_WIDTH = 480;

type VehicleTextChangeEvent = ChangeEvent<
  HTMLInputElement | HTMLTextAreaElement
>;

interface VehicleDrawerProps {
  open: boolean;
  /** The vehicle being edited; null opens an empty form that adds one. */
  vehicle: Vehicle | null;
  /** Where a new vehicle goes. Only MASTER / ADMIN pass it; everyone else adds to their own branch. */
  targetBranchUuid?: string;
  /** The branch a new vehicle lands in, named so it is never filed in the wrong one. */
  targetBranchLabel: string;
  onClose: () => void;
  onSaved: () => void;
}

export default function VehicleDrawer({
  open,
  vehicle,
  targetBranchUuid,
  targetBranchLabel,
  onClose,
  onSaved,
}: VehicleDrawerProps) {
  const [form, setForm] = useState<VehicleForm>(INITIAL_VEHICLE_FORM);
  const [errors, setErrors] = useState<VehicleFormErrors>({});
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    if (!open) return;
    setForm(vehicle ? toVehicleForm(vehicle) : INITIAL_VEHICLE_FORM);
    setErrors({});
  }, [open, vehicle]);

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleTextChange =
    (field: keyof VehicleForm) => (event: VehicleTextChangeEvent) => {
      const { value } = event.target;
      setForm((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => ({ ...prev, [field]: "" }));
    };

  const saveVehicle = async () => {
    const request = toVehicleRequest(form);
    if (vehicle) {
      await updateVehicle(vehicle.uuid, request);
      return;
    }
    await createVehicle(request, targetBranchUuid);
  };

  const handleSubmit = async () => {
    const newErrors = validateVehicleForm(form);
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setSaving(true);
    try {
      await saveVehicle();
      onSaved();
      onClose();
    } catch {
      // The axios interceptor reports the failure; the drawer stays open so the
      // input can be corrected and saved again.
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      slotProps={{
        paper: {
          sx: { width: { xs: "100%", md: DRAWER_WIDTH }, maxWidth: "100%", p: 0 },
        },
      }}
    >
      <Stack sx={{ height: "100%" }}>
        <Box sx={{ p: 3, pb: 0, [compact]: { p: 2, pb: 0 } }}>
          <Typography variant="body1" fontWeight={600} gutterBottom>
            {vehicle ? "แก้ไขรถ" : "เพิ่มรถ"}
          </Typography>
          <Divider />
        </Box>

        <Box sx={{ p: 3, [compact]: { p: 2 }, overflowY: "auto", flex: 1 }}>
          {!vehicle && (
            <Alert severity="info" sx={{ mb: 2 }}>
              เพิ่มรถให้สาขา {targetBranchLabel}
            </Alert>
          )}

          <FieldLabel required>ป้ายทะเบียน</FieldLabel>
          <TextField
            size="small"
            fullWidth
            value={form.vehicle_plate}
            onChange={handleTextChange("vehicle_plate")}
            placeholder="กรอกป้ายทะเบียน"
            error={Boolean(errors.vehicle_plate)}
            helperText={errors.vehicle_plate || VEHICLE_PLATE_EXAMPLE}
            sx={inputSx}
          />

          <FieldLabel>ประเภทรถ</FieldLabel>
          <TextField
            size="small"
            fullWidth
            value={form.vehicle_type}
            onChange={handleTextChange("vehicle_type")}
            placeholder="เช่น กระบะ, รถตู้"
            slotProps={{ htmlInput: { maxLength: VEHICLE_TEXT_MAX_LENGTH } }}
            sx={inputSx}
          />

          <FieldLabel>รุ่นรถ/ยี่ห้อรถ</FieldLabel>
          <TextField
            size="small"
            fullWidth
            value={form.model}
            onChange={handleTextChange("model")}
            placeholder="กรอกรุ่นรถ/ยี่ห้อรถ"
            slotProps={{ htmlInput: { maxLength: VEHICLE_TEXT_MAX_LENGTH } }}
            sx={inputSx}
          />

          <FieldLabel>ประเภทเครื่องยนต์</FieldLabel>
          <TextField
            select
            size="small"
            fullWidth
            value={form.fuel_type}
            onChange={handleTextChange("fuel_type")}
            sx={inputSx}
          >
            <MenuItem value="">ไม่ระบุ</MenuItem>
            {FUEL_TYPE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <FieldLabel>ปริมาตรถังน้ำมัน (ลิตร)</FieldLabel>
          <TextField
            size="small"
            fullWidth
            type="number"
            value={form.capacity}
            onChange={handleTextChange("capacity")}
            placeholder="กรอกปริมาตรถังน้ำมัน"
            error={Boolean(errors.capacity)}
            helperText={errors.capacity}
            sx={inputSx}
          />
        </Box>

        <Stack
          direction="row"
          justifyContent="flex-end"
          gap={2}
          sx={{
            p: 3,
            pt: 2,
            [compact]: { p: 2, pt: 1.5 },
            borderTop: (theme) => `1px solid ${theme.palette.draft.main}`,
          }}
        >
          <Button disabled={saving} onClick={handleClose}>
            ยกเลิก
          </Button>
          <Button loading={saving} variant="contained" onClick={handleSubmit}>
            บันทึก
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
}
